import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/react';
import { projects } from '../services/api';
import ProjectModal from '../components/ProjectModal';
import BarList from '../components/BarList';
import Sidebar from '../components/Sidebar';
import { IconFolder, IconCheck, IconTransfer, IconUsers } from '../components/icons';
import { confirmDelete, confirmStop, showError } from '../services/alerts';

const STATUS = {
  done:    { label: '✓ Done',    color: 'text-good',  dot: 'bg-good',  track: 'bg-good/15' },
  running: { label: '⟳ Running', color: 'text-warn',  dot: 'bg-warn',  track: 'bg-warn/15' },
  failed:  { label: '✗ Failed',  color: 'text-bad',   dot: 'bg-bad',   track: 'bg-bad/15' },
  pending: { label: '● Pending', color: 'text-accent', dot: 'bg-accent', track: 'bg-accent/15' },
};
const statusMeta = s => STATUS[s] || STATUS.pending;

const SKELETON_STAT = () => (
  <div className="rounded-xl border border-border2 bg-surface px-5 py-4">
    <div className="mb-3 h-8 w-8 rounded-lg bg-surface2 motion-safe:animate-pulse" />
    <div className="mb-2 h-2.5 w-20 rounded bg-surface2 motion-safe:animate-pulse" />
    <div className="h-6 w-14 rounded bg-surface2 motion-safe:animate-pulse" />
  </div>
);
const SKELETON_ROW = () => (
  <div className="mb-2.5 rounded-xl border border-border2 bg-surface px-5 py-4">
    <div className="mb-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-surface2 motion-safe:animate-pulse" />
      <div className="flex-1">
        <div className="mb-1.5 h-3.5 w-40 rounded bg-surface2 motion-safe:animate-pulse" />
        <div className="h-3 w-28 rounded bg-surface2 motion-safe:animate-pulse" />
      </div>
    </div>
    <div className="h-1 w-full rounded-full bg-surface2 motion-safe:animate-pulse" />
  </div>
);

export default function Dashboard() {
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const { user } = useUser();
  const { orgId } = useAuth();
  const navigate = useNavigate();

  const load = () => projects.list().then(r => setProjectList(r.data)).finally(() => setLoading(false));
  // Refetch whenever the active Clerk workspace changes (personal <-> org,
  // or switching between orgs), since every list here is scoped server-side
  // to whichever one is active -- see services/authz.js.
  useEffect(() => { load(); }, [orgId]);

  const handleEdit = (e, project) => {
    e.stopPropagation();
    setEditingProject(project);
  };

  const handleDelete = async (e, project) => {
    e.stopPropagation();
    if (!(await confirmDelete(project.name))) return;
    try {
      await projects.delete(project.id);
      load();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const handleStop = async (e, project) => {
    e.stopPropagation();
    if (!(await confirmStop(project.name))) return;
    try {
      await projects.stop(project.id);
      load();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to stop project');
    }
  };

  const totalBytes = projectList.reduce((a, p) => a + parseInt(p.total_bytes || 0), 0);
  const totalDone = projectList.reduce((a, p) => a + parseInt(p.done_count || 0), 0);
  const totalAccs = projectList.reduce((a, p) => a + parseInt(p.total_accounts || 0), 0);

  const formatGiB = b => (b / 1073741824).toFixed(2) + ' GiB';

  const byDataTransferred = [...projectList]
    .map(p => ({ key: p.id, label: p.name, value: parseInt(p.total_bytes || 0), display: formatGiB(parseInt(p.total_bytes || 0)) }))
    .sort((a, b) => b.value - a.value)
    .map(r => ({ ...r, color: 'bg-accent', track: 'bg-accent/15' }));

  const byStatus = Object.entries(
    projectList.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {})
  )
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => {
      const meta = statusMeta(status);
      return { key: status, label: meta.label, value: count, display: String(count), color: meta.dot, track: meta.track };
    });

  return (
    <div className="flex h-screen bg-bg font-sans text-ink">
      <Sidebar active="dashboard" />

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex min-h-16 items-center gap-4 border-b border-border2 px-6 py-3">
          <div className="flex-1">
            <div className="font-display text-base font-bold">Dashboard</div>
            <div className="text-xs text-dim">
              Welcome back{user?.firstName ? `, ${user.firstName}` : ''} — here's what's syncing.
            </div>
          </div>
          <button onClick={() => setShowCreateModal(true)}
            className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-white shadow-[0_0_16px_rgba(245,47,85,.2)]
              transition-colors hover:bg-accent2">
            + New Project
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Stats */}
          <div className="mb-6 grid grid-cols-4 gap-3">
            {loading ? [0, 1, 2, 3].map(i => <SKELETON_STAT key={i} />) : [
              ['Projects', projectList.length, IconFolder, 'text-accent', 'bg-accent/10'],
              ['Accounts done', totalDone, IconCheck, 'text-good', 'bg-good/10'],
              ['Data transferred', formatGiB(totalBytes), IconTransfer, 'text-ink', 'bg-surface2'],
              ['Total accounts', totalAccs, IconUsers, 'text-cyan', 'bg-cyan/10'],
            ].map(([label, val, Icon, color, chip]) => (
              <div key={label} className="rounded-xl border border-border2 bg-surface px-5 py-4">
                <div className={`mb-3 flex h-8 w-8 items-center justify-center rounded-lg ${chip} ${color}`}>
                  <Icon />
                </div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-dim">{label}</div>
                <div className={`text-2xl font-semibold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          {/* Charts */}
          {!loading && projectList.length > 0 && (
            <div className="mb-6 grid grid-cols-2 gap-3">
              <BarList title="Data transferred by project" rows={byDataTransferred} labelWidth="w-40" />
              <BarList title="Projects by status" rows={byStatus} labelWidth="w-40" />
            </div>
          )}

          {/* Projects */}
          <div className="mb-3.5 font-display text-sm font-bold">Projects</div>
          {loading ? (
            [0, 1, 2].map(i => <SKELETON_ROW key={i} />)
          ) : projectList.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border2 bg-surface px-10 py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-2xl">📧</div>
              <div className="font-display text-base font-bold">No projects yet</div>
              <div className="max-w-sm text-sm text-muted">
                Create your first migration project to start moving mailboxes from cPanel to Plesk.
              </div>
              <button onClick={() => setShowCreateModal(true)}
                className="mt-1 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent2">
                + New Project
              </button>
            </div>
          ) : (
            projectList.map(p => {
              const meta = statusMeta(p.status);
              return (
                <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                  className="mb-2.5 cursor-pointer rounded-xl border border-border2 bg-surface px-5 py-4 transition-all
                    hover:border-accent/40 hover:shadow-[0_8px_24px_-14px_rgba(0,0,0,.6)] motion-safe:hover:-translate-y-0.5">
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface2 text-lg">📧</div>
                    <div>
                      <div className="text-sm font-bold">{p.name}</div>
                      <div className="font-mono text-xs text-dim">{p.domain} · {p.src_platform === 'custom' ? 'Custom IMAP' : 'cPanel'} → {p.dst_platform === 'cpanel' ? 'cPanel' : p.dst_platform === 'custom' ? 'Custom IMAP' : 'Plesk'}</div>
                    </div>
                    <div className={`ml-auto rounded-full bg-surface2 px-2.5 py-1 text-[11px] font-semibold ${meta.color}`}>
                      {meta.label}
                    </div>
                    {p.status === 'running' && (
                      <button onClick={e => handleStop(e, p)}
                        className="rounded-md border border-warn/30 px-2 py-1 text-xs text-warn transition-colors hover:bg-warn/10">
                        ■ Stop
                      </button>
                    )}
                    <button onClick={e => handleEdit(e, p)} disabled={p.status === 'running'}
                      title={p.status === 'running' ? 'Cannot edit while a sync is in progress' : 'Edit project'}
                      className="rounded-md border border-border2 px-2 py-1 text-xs text-dim transition-colors
                        hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
                      ✎
                    </button>
                    <button onClick={e => handleDelete(e, p)} disabled={p.status === 'running'}
                      title={p.status === 'running' ? 'Cannot delete while a sync is in progress' : 'Delete project'}
                      className="rounded-md border border-border2 px-2 py-1 text-xs text-dim transition-colors
                        hover:border-bad/40 hover:text-bad disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
                      🗑
                    </button>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-[11px] text-dim">Progress</span>
                    <div className={`h-1 flex-1 overflow-hidden rounded-full ${meta.track}`}>
                      <div className={`h-full rounded-full transition-all duration-500 ${meta.dot}`}
                        style={{ width: p.total_accounts > 0 ? `${(p.done_count / p.total_accounts) * 100}%` : '0%' }} />
                    </div>
                    <span className="font-mono text-[11px] text-muted">{p.done_count}/{p.total_accounts}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showCreateModal && (
        <ProjectModal onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); load(); }} />
      )}
      {editingProject && (
        <ProjectModal project={editingProject} onClose={() => setEditingProject(null)}
          onSaved={() => { setEditingProject(null); load(); }} />
      )}
    </div>
  );
}
